/**
 * «Что нового»: выпуски пишет персонал (Настройки → Персоналу → «Что нового»), люди видят
 * карточку над панелью пользователя и историю в «Поддержке и новостях».
 */
import { authed } from './auth'
import { BASE } from './api'
import { telegramHtml, telegramText } from './telegramText'
import type { Snowflake } from '@/types/api'

export interface ReleaseItem {
  /** Текст в разметке чата. */
  text: string
  /** Имя загруженной картинки: <хэш>.<расширение>. */
  image?: string
}

export interface Release {
  id: Snowflake
  title: string
  items: ReleaseItem[]
  /** null — черновик. */
  published_at: string | null
  updated_at: string
}

export interface StaffRelease extends Release {
  created_at: string
  unpublished_at: string | null
  /** Показывается карточкой в приложении. */
  card: boolean
  /** Есть пост в #обновления официального сервера. */
  official: boolean
  /** Пост в канал не вышел, не обновился или не удалился — сам выпуск сохранён. */
  official_error?: string
  /** Видели карточку: открыли или закрыли. */
  seen: number
  opened: number
  /** Могли увидеть: зарегистрированы до выхода. */
  audience: number
}

export interface ReleaseInput {
  title: string
  items: ReleaseItem[]
}

/** Пределы сервера (internal/releases). Пунктов советуем 3–5. */
export const RELEASE_LIMITS = { title: 100, items: 6, text: 600, imageBytes: 700 << 10 } as const

export const releaseImageUrl = (name: string) => `${BASE}/release-images/${name}`

const json = (method: string, body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })

export const releasesApi = {
  list: () => authed<{ releases: Release[] }>('/releases'),
  pending: () => authed<{ release: Release | null }>('/releases/pending'),
  pendingById: (id: Snowflake) => authed<{ release: Release | null }>(`/releases/${id}/pending`),
  seen: (id: Snowflake, opened: boolean) => authed<void>(`/releases/${id}/seen`, json('POST', { opened })),
  staff: () => authed<{ releases: StaffRelease[] }>('/moderation/releases'),
  create: (input: ReleaseInput) => authed<StaffRelease>('/moderation/releases', json('POST', input)),
  update: (id: Snowflake, input: ReleaseInput) => authed<StaffRelease>(`/moderation/releases/${id}`, json('PATCH', input)),
  publish: (id: Snowflake, opts: { card: boolean; post_official: boolean }) => authed<StaffRelease>(`/moderation/releases/${id}/publish`, json('POST', opts)),
  unpublish: (id: Snowflake) => authed<StaffRelease>(`/moderation/releases/${id}/unpublish`, json('POST')),
  remove: (id: Snowflake) => authed<void>(`/moderation/releases/${id}`, json('DELETE')),
  uploadImage: (dataUri: string) => authed<{ name: string }>('/moderation/releases/images', json('POST', { image: dataUri })),
}

/** «24 сентября», а для прошлых лет — «24 сентября 2025». */
export function releaseDate(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date()
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('ru-RU', sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' })
}

function readDataUri(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('Не удалось прочитать картинку'))
    r.readAsDataURL(file)
  })
}

/** Сколько байт в data URI после декодирования base64. */
const dataUriBytes = (uri: string) => Math.floor(((uri.length - uri.indexOf(',') - 1) * 3) / 4)

/**
 * Картинка пункта: вписываем в 1600×1600 и сжимаем в WebP, где браузер его не умеет — в JPEG.
 * Скриншот 2560×1440 в PNG весит мегабайты, а в WebP при 1600 по ширине — 100–300 КБ.
 * Небольшой GIF отправляем как есть, чтобы не потерять анимацию.
 */
export async function fitReleaseImage(file: File): Promise<string> {
  if (file.type === 'image/gif' && file.size <= RELEASE_LIMITS.imageBytes) return readDataUri(file)
  const bitmap = await createImageBitmap(file)
  try {
    for (const [side, quality] of [[1600, 0.86], [1600, 0.72], [1280, 0.7], [1024, 0.6]] as const) {
      const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Не удалось обработать картинку')
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      let uri = canvas.toDataURL('image/webp', quality)
      if (!uri.startsWith('data:image/webp')) uri = canvas.toDataURL('image/jpeg', quality)
      if (dataUriBytes(uri) <= RELEASE_LIMITS.imageBytes) return uri
    }
    throw new Error('Картинка не сжимается до 700 КБ — возьмите скриншот поменьше')
  } finally {
    bitmap.close()
  }
}

/**
 * Старый путь через событие copy: работает и там, где асинхронный буфер запрещён политикой
 * страницы, — нужен только сам клик. Кладёт и простой текст, и HTML.
 */
function legacyCopy(plain: string, html: string): boolean {
  const onCopy = (e: ClipboardEvent) => {
    e.clipboardData?.setData('text/plain', plain)
    e.clipboardData?.setData('text/html', html)
    e.preventDefault()
  }
  const area = document.createElement('textarea')
  area.value = plain
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.append(area)
  area.select()
  document.addEventListener('copy', onCopy)
  try {
    return document.execCommand('copy')
  } finally {
    document.removeEventListener('copy', onCopy)
    area.remove()
  }
}

/** Кладёт текст для Telegram в буфер: и простой, и с форматированием. */
export async function copyForTelegram(input: ReleaseInput): Promise<void> {
  const plain = telegramText(input)
  const html = telegramHtml(input)
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ])
      return
    } catch {
      // запасные пути ниже
    }
  }
  if (legacyCopy(plain, html)) return
  await navigator.clipboard.writeText(plain)
}
